import { useEffect } from 'react';
import type { ChatRole } from '@/entities/Chat/model/types';
import type { UserChatProfile } from '@/entities/UserChat/model/types';
import { getUserChatProfileName } from '@/entities/UserChat/mappers';
import { useUserChatDirectory } from '@/entities/UserChat/model/directoryStore';
import { Paginator } from '@/shared/ui/Paginator/Paginator';
import { RequestStatus } from '@/shared/model/requestStatus';
import { formatChatListTimestamp } from '@/entities/Chat/model/selectors';
import { RequestEmpty, RequestErrorBanner, RequestLoading } from '@/shared/ui/RequestState';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import workspaceClasses from '@/entities/Chat/ui/ChatWorkspace.module.scss';
import classes from './UserChatsPage.module.scss';

interface UserDirectoryProps {
    role: ChatRole;
    selectedProfileId: string | null;
    onSelect: (profile: UserChatProfile) => void;
}

/** Показывает пользователей постранично для выбора переписки */
export const UserDirectory = ({ role, selectedProfileId, onSelect }: UserDirectoryProps) => {
    const profiles = useUserChatDirectory((state) => state.profiles);
    const totalCount = useUserChatDirectory((state) => state.totalCount);
    const page = useUserChatDirectory((state) => state.page);
    const pageSize = useUserChatDirectory(state => state.pageSize);
    const setPageSize = useUserChatDirectory(state => state.setPageSize);
    const loadedRole = useUserChatDirectory((state) => state.role);
    const status = useUserChatDirectory((state) => state.status);
    const error = useUserChatDirectory((state) => state.error);
    const load = useUserChatDirectory((state) => state.load);
    const reset = useUserChatDirectory((state) => state.reset);
    const activity = useUserChatDirectory((state) => state.activity);
    const isLoading = loadedRole !== role || status === RequestStatus.Loading;

    useEffect(() => {
        void load(role);
    }, [role, load]);

    useEffect(() => reset, [reset]);

    return (
        <aside
            className={`${workspaceClasses.chatListPanel} ${classes.directory}`}
            aria-label="Все пользователи"
        >
            <header className={workspaceClasses.chatListHeader}>
                <div className={classes.directoryTitle}>
                    <h2>
                        {role === 'Client'
                            ? 'Клиенты'
                            : 'Исполнители'}
                    </h2>
                    <ActionButton
                        icon="pi pi-refresh"
                        iconOnly
                        size="small"
                        color="secondary"
                        aria-label="Обновить список пользователей"
                        disabled={isLoading}
                        onClick={() => void load(role, page, true)}
                    />
                </div>
                <span className={classes.directoryCount}>
                    {status === RequestStatus.Success && loadedRole === role
                        ? `Всего: ${totalCount} · по ${pageSize}`
                        : `По ${pageSize} на странице`}
                </span>
                <span className={classes.directoryCount}>Свежие сообщения сверху</span>
            </header>
            <div className={workspaceClasses.chatListBody}>
                {isLoading && profiles.length === 0 && (
                    <RequestLoading message="Загружаем пользователей..." />
                )}
                <RequestErrorBanner
                    status={status}
                    error={error}
                    fallbackMessage="Не удалось загрузить пользователей"
                    onRetry={() => load(role, page)}
                />
                {!isLoading && status === RequestStatus.Success && profiles.length === 0 && (
                    <RequestEmpty message="Пользователи не найдены" />
                )}
                {loadedRole === role && profiles.length > 0 && (
                    <ul className={workspaceClasses.chatItems}>
                        {profiles.map((selection, index) => (
                            <li key={selection.profile.id ?? index}>
                                <button
                                    type="button"
                                    className={`${workspaceClasses.chatItem} ${selection.profile.id === selectedProfileId
                                        ? workspaceClasses.chatItemSelected
                                        : ''}`}
                                    aria-current={selection.profile.id === selectedProfileId
                                        ? 'true'
                                        : undefined
                                    }
                                    disabled={!selection.profile.id}
                                    onClick={() => onSelect(selection)}
                                >
                                    <span className={workspaceClasses.chatItemContent}>
                                        <span className={workspaceClasses.chatName}>
                                            {getUserChatProfileName(selection)}
                                        </span>
                                        <span className={workspaceClasses.chatPreview}>
                                            {selection.profile.phone || 'Телефон не указан'}
                                        </span>
                                        {selection.profile.appUserId && activity[selection.profile.appUserId] && (
                                            <span className={classes.userActivity}>
                                                <span>Диалогов: {activity[selection.profile.appUserId]?.dialogCount}
                                                </span>
                                                <span>
                                                    {activity[selection.profile.appUserId]?.lastMessageAt != null
                                                        ? `Последнее: ${formatChatListTimestamp(activity[selection.profile.appUserId]?.lastMessageAt ?? null)}`
                                                        : 'Сообщений пока нет'}
                                                </span>
                                            </span>
                                        )}
                                        {selection.profile.appUserId && activity[selection.profile.appUserId] === null && (
                                            <span className={classes.userActivity}>Статистика недоступна</span>
                                        )}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <Paginator
                label="Страницы пользователей"
                first={page * pageSize}
                rows={pageSize}
                totalRecords={totalCount}
                disabled={isLoading || status !== RequestStatus.Success}
                onPageChange={event => {
                    if (event.rows !== pageSize) setPageSize(event.rows);
                    else void load(role, event.page);
                }}
            />
        </aside>
    );
};
